import type { Message } from '@centraoneplus/shared-types';

/**
 * Pure helpers backing the conversation-thread render (ConversationThread.tsx).
 *
 * Kept side-effect-free and DOM-light so they can be unit-tested directly
 * (email-render.logic.test.ts) instead of through a verbatim mirror.
 */

// ─── 1. Ordering ─────────────────────────────────────────────────────────────

/**
 * Return messages in chronological ASCENDING order (oldest first, newest last).
 *
 * The messages API already returns ascending order, so this is a defensive,
 * STABLE sort: it never reverses the API order and preserves the relative
 * order of messages that share an identical `createdAt` (a stable sort on an
 * already-sorted array is a no-op). It exists only to harden against an
 * upstream change that might return an unsorted page — it must NOT be relied on
 * to re-derive ordering the backend deliberately set (e.g. the event-time fix
 * that floats the originating customer email first).
 */
export function sortMessagesAscending(messages: Message[]): Message[] {
  // Index-tagged sort → stable on engines whose Array.prototype.sort isn't.
  return messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => {
      const ta = new Date(a.message.createdAt).getTime();
      const tb = new Date(b.message.createdAt).getTime();
      // NaN-safe: unparseable timestamps keep their original relative position.
      if (!Number.isFinite(ta) || !Number.isFinite(tb) || ta === tb) {
        return a.index - b.index;
      }
      return ta - tb;
    })
    .map((entry) => entry.message);
}

// ─── 2. Templated system-notification allowlist ──────────────────────────────

/**
 * `metadata.emailType` values that identify a TEMPLATED system notification
 * (the customer-facing transactional emails) which should collapse to a chip.
 *
 * IMPORTANT: collapse ONLY by this allowlist. The system also posts a
 * content-aware acknowledgement (NO `ticket_confirmation`/templated emailType —
 * it asks the customer for missing info) that must stay fully visible. Gating
 * on the allowlist (not `senderType === 'system'`) is what keeps that
 * acknowledgement expanded.
 *
 * Add more templated types here as they ship (csat survey, sla breach, …).
 */
export const COLLAPSIBLE_EMAIL_TYPES: ReadonlySet<string> = new Set<string>([
  'ticket_confirmation',
  'ticket_resolved',
  'ticket_update',
  'sla_breach',
  'csat_survey',
  'csat_request',
]);

/**
 * True when a message is a templated system notification that should render as
 * a collapsed chip (expandable to its full sandboxed body).
 *
 * Requires BOTH `senderType === 'system'` AND an allowlisted `metadata.emailType`.
 * A system message with no emailType, or an emailType not on the allowlist
 * (e.g. the content-aware acknowledgement), returns false → renders in full.
 */
export function isCollapsibleSystemNotification(message: Message): boolean {
  if (message.senderType !== 'system') return false;
  const emailType = message.metadata?.emailType;
  return typeof emailType === 'string' && COLLAPSIBLE_EMAIL_TYPES.has(emailType);
}

/**
 * The set of messages the Conversation tab actually renders: everything
 * EXCEPT the automated ticket-confirmation acknowledgement, which is
 * deliberately hidden from the thread (it's not part of the agent/customer
 * dialogue and its chip would just add noise — see ConversationThread.tsx).
 *
 * Bug #14955: the tab's badge counted the raw, unfiltered message array, so
 * a ticket whose ONLY message was the confirmation email showed "Conversation
 * 1" next to the thread's own "No messages in this conversation yet." empty
 * state. Any caller computing a message COUNT for display (a tab badge, a
 * summary line) should filter through this first so it agrees with what the
 * thread renders, exactly as ConversationThread.tsx's own `orderedMessages`
 * does internally.
 */
export function filterDisplayableMessages(messages: Message[]): Message[] {
  return messages.filter((m) => m.metadata?.emailType !== 'ticket_confirmation');
}

// ─── 2b. Inline (cid:) image resolution ───────────────────────────────────────

/**
 * The `fileId` of the attachment an inbound email's `cid:<contentId>` image
 * reference points at, or null when no attachment carries that Content-ID (or
 * it has no stored file) so the caller can fall back to its "not displayable"
 * placeholder.
 *
 * Bug #9197: the browser can't fetch the `cid:` scheme itself
 * (`net::ERR_UNKNOWN_URL_SCHEME`). CO-004: nor can an `<img>` fetch the stored
 * file at `/api/files/{fileId}/download` (no Bearer token → 401), so callers
 * fetch the bytes by this id through the authenticated client.
 *
 * Content-IDs are matched case-insensitively with any RFC 2392 angle
 * brackets stripped, since both the `cid:` src and the stored attachment
 * value may or may not include them.
 */
export function findCidAttachmentFileId(
  cidSrc: string,
  attachments: Message['attachments'] | undefined,
): string | null {
  const cid = cidSrc
    .replace(/^cid:/i, '')
    .replace(/^<|>$/g, '')
    .toLowerCase();
  if (!cid || !attachments?.length) return null;

  for (const a of attachments) {
    const att = a as typeof a & { contentId?: string; fileId?: string };
    if (!att.contentId || !att.fileId) continue;
    if (att.contentId.replace(/^<|>$/g, '').toLowerCase() === cid) {
      return att.fileId;
    }
  }
  return null;
}

/**
 * CO-004: the fileIds of every attachment an email body references inline via
 * `<img src="cid:...">`, de-duplicated. An `<img>` can't fetch
 * `/api/files/<id>/download` itself (it sends no Bearer token, so the download
 * 401s), so the caller fetches these through the authenticated client and
 * swaps in `blob:` URLs. Empty for any body without cid: images.
 */
export function collectInlineCidFileIds(
  html: string | undefined,
  attachments: Message['attachments'] | undefined,
): string[] {
  if (!html || !attachments?.length || !/cid:/i.test(html)) return [];
  const ids = new Set<string>();
  for (const m of html.matchAll(/src\s*=\s*["']?\s*(cid:[^"'\s>]+)/gi)) {
    const fileId = findCidAttachmentFileId(m[1], attachments);
    if (fileId) ids.add(fileId);
  }
  return [...ids];
}

// ─── 3. Quoted-history / signature trimming ──────────────────────────────────

export interface SplitContent {
  /** The substantive body shown by default. */
  visible: string;
  /** Trailing quoted-reply history / signature, hidden behind a toggle. `''` when nothing was trimmed. */
  trimmed: string;
  /** True when `trimmed` is non-empty (the toggle should render). */
  hasTrimmed: boolean;
}

/**
 * Markers that begin a quoted-reply block or signature. Conservative on
 * purpose — a false negative (showing a bit of quoted text) is harmless, a
 * false positive (hiding real content) is not.
 */
const QUOTE_BOUNDARY_PATTERNS: RegExp[] = [
  // "On Mon, Jan 2, 2024 at 3:04 PM Alice <a@x.com> wrote:" (and localized-ish variants)
  /On\s.{0,200}?\bwrote:/i,
  // Outlook / many clients: a "From: … Sent: … To: …" header block.
  /(?:^|\n)\s*-{2,}\s*Original Message\s*-{2,}/i,
  /(?:^|\n)\s*From:\s.+\n(?:\s*(?:Sent|Date|To|Subject):\s.+\n){1,}/i,
  // Forwarded marker.
  /(?:^|\n)\s*-{2,}\s*Forwarded message\s*-{2,}/i,
];

/** Signature delimiter — a line that is exactly "-- " (RFC 3676) or "--". */
const SIGNATURE_PATTERN = /(?:^|\n)--\s?\n/;

/**
 * Find the earliest index of any quote/signature boundary in plain text.
 * Returns -1 when none is found.
 */
function firstBoundaryIndex(text: string): number {
  let earliest = -1;
  for (const re of [...QUOTE_BOUNDARY_PATTERNS, SIGNATURE_PATTERN]) {
    const m = re.exec(text);
    if (m && (earliest === -1 || m.index < earliest)) {
      earliest = m.index;
    }
  }
  return earliest;
}

/**
 * Split PLAIN-TEXT content into the substantive body and trailing quoted /
 * signature history.
 *
 * Best-effort and conservative: only splits when a boundary is found AND it
 * leaves a non-trivial visible body (≥ 1 non-whitespace char before the
 * boundary), so a message that is ENTIRELY a quote is shown in full rather
 * than collapsed to nothing.
 */
export function splitTrimmedText(content: string): SplitContent {
  const none: SplitContent = { visible: content, trimmed: '', hasTrimmed: false };
  if (!content) return none;

  const idx = firstBoundaryIndex(content);
  if (idx <= 0) return none; // -1 (no boundary) or 0 (boundary at very start → nothing to show)

  const visible = content.slice(0, idx);
  const trimmed = content.slice(idx);
  if (!visible.trim() || !trimmed.trim()) return none;

  return { visible: visible.replace(/\s+$/, ''), trimmed, hasTrimmed: true };
}

/**
 * Split HTML content into substantive body + trailing quoted/signature block.
 *
 * Strategy (DOM-based, runs only in the browser):
 *   1. Prefer the first quote container clients emit — `<blockquote>`,
 *      Gmail's `.gmail_quote`, or an Outlook `#divRdfMmDeliveryReport` /
 *      `#appendonsend` boundary. Everything from that node onward is trimmed.
 *   2. Otherwise fall back to a plain-text boundary scan on the rendered text
 *      and, if found, trim from the first matching top-level node.
 *
 * Conservative: if splitting would leave an empty visible body, returns the
 * whole thing as visible (`hasTrimmed: false`).
 */
export function splitTrimmedHtml(html: string): SplitContent {
  const none: SplitContent = { visible: html, trimmed: '', hasTrimmed: false };
  if (!html || typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return none;
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;

    // 1. Quote-container detection. A single combined selector returns matches
    // in document order, so the first match is the earliest-occurring boundary.
    const quoteSelector = [
      'blockquote',
      '.gmail_quote',
      '.gmail_extra',
      '#appendonsend',
      '#divRplyFwdMsg',
      'div[id^="x_appendonsend"]',
    ].join(',');
    const boundaryNode: Element | null = body.querySelector(quoteSelector);

    if (boundaryNode) {
      // Promote the boundary to a top-level child of body so we can split the
      // sibling stream cleanly.
      let top: Node = boundaryNode;
      while (top.parentNode && top.parentNode !== body) top = top.parentNode;
      const split = splitBodyAtNode(doc, body, top);
      if (split) return split;
    }

    // 2. Plain-text fallback over the rendered text.
    const textIdx = firstBoundaryIndex(body.textContent || '');
    if (textIdx > 0) {
      const node = findNodeAtTextOffset(body, textIdx);
      if (node) {
        let top: Node = node;
        while (top.parentNode && top.parentNode !== body) top = top.parentNode;
        const split = splitBodyAtNode(doc, body, top);
        if (split) return split;
      }
    }

    return none;
  } catch {
    return none;
  }
}

/**
 * Serialize the body children before `topNode` as `visible` and `topNode`
 * onward as `trimmed`. Returns null when the split is degenerate (empty
 * visible).
 */
function splitBodyAtNode(doc: Document, body: HTMLElement, topNode: Node): SplitContent | null {
  const children = Array.from(body.childNodes);
  const cut = children.indexOf(topNode as ChildNode);
  if (cut <= 0) return null; // boundary is first child → nothing substantive before it

  const visibleFrag = doc.createElement('div');
  const trimmedFrag = doc.createElement('div');
  children.forEach((child, i) => {
    (i < cut ? visibleFrag : trimmedFrag).appendChild(child.cloneNode(true));
  });

  if (!visibleFrag.textContent?.trim() || !trimmedFrag.textContent?.trim()) return null;
  return { visible: visibleFrag.innerHTML, trimmed: trimmedFrag.innerHTML, hasTrimmed: true };
}

/** Walk the body in document order to find the element containing text offset. */
function findNodeAtTextOffset(body: HTMLElement, offset: number): Node | null {
  let acc = 0;
  const walk = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length || 0;
      if (acc + len >= offset) return node;
      acc += len;
      return null;
    }
    for (const child of Array.from(node.childNodes)) {
      const hit = walk(child);
      if (hit) return hit;
    }
    return null;
  };
  return walk(body);
}

// ─── 3b. Length clamp (collapse long email bodies) ───────────────────────────

/**
 * Max rendered height (px) an email body may occupy before it is clamped behind
 * a "Show more" toggle. ~320px ≈ 14–16 lines at the iframe's 14px/1.5 base.
 *
 * Clamp is applied on the OUTER wrapper around the iframe (NOT inside the
 * iframe): when collapsed the visible area is this height with a bottom fade;
 * when expanded it is the iframe's full measured content height.
 */
export const EMAIL_CLAMP_MAX_HEIGHT = 320;

/**
 * Whether a measured email body should offer a length-collapse toggle.
 *
 * `contentHeight` is the iframe's auto-sized content height (px). A small
 * tolerance avoids a pointless toggle when the body is only a few pixels over
 * the threshold (a toggle that reveals one extra line is just noise).
 */
export function shouldClampEmailBody(
  contentHeight: number,
  maxHeight: number = EMAIL_CLAMP_MAX_HEIGHT,
): boolean {
  if (!Number.isFinite(contentHeight) || contentHeight <= 0) return false;
  const tolerance = 24; // ~one line of slack
  return contentHeight > maxHeight + tolerance;
}

// ─── 4. Sandbox iframe document ───────────────────────────────────────────────

/**
 * A background declaration carrying a REAL color (not `transparent`/`inherit`/…).
 * `bgcolor="#fff"` is the legacy table attribute branded templates still emit.
 */
const OWN_BACKGROUND_PATTERN =
  /bgcolor\s*=\s*["']?#?[0-9a-z]|background(?:-color)?\s*:\s*(?:#[0-9a-f]{3,8}|rgba?\(|hsla?\(|(?!transparent|none|inherit|initial|unset|revert)[a-z]+)/i;

/**
 * True when the email brings its OWN canvas — a full `<!DOCTYPE html>` document,
 * or a fragment that paints its own background colors (branded transactional
 * templates: header bar, white body, colored CTA buttons).
 *
 * Such an email is a self-contained design and must be rendered on a LIGHT
 * canvas in BOTH themes, with its colors left untouched. Recoloring it for dark
 * mode is what produced near-white text on the template's own white body.
 * Mail clients (Gmail, Outlook) treat designed HTML emails the same way.
 *
 * A bare reply fragment (no background of its own) returns false and keeps the
 * dark-mode treatment, which is what makes Outlook's hard-coded black inline
 * text readable on our dark surface.
 */
export function emailHasOwnCanvas(html: string): boolean {
  if (!html) return false;
  if (/<html[\s>]/i.test(html) || /<!doctype/i.test(html)) return true;
  return OWN_BACKGROUND_PATTERN.test(html);
}

/**
 * Wrap an email HTML body (which may be a full `<!DOCTYPE html>…<style>…`
 * document OR a fragment) into a `srcdoc` for a sandboxed iframe.
 *
 * The iframe is rendered with `sandbox=""` (NO `allow-scripts`), so anything in
 * the email — scripts, its own `<style>`, full-width tables, background colors
 * — is fully isolated from the app layout and cannot execute. We inject a small
 * base stylesheet for readable defaults + word-wrap, and a height-reporter that
 * runs via the parent (the iframe itself has no script capability).
 *
 * Because scripts are disabled inside the frame, height is measured by the
 * PARENT after load (see EmailHtmlFrame.tsx) reading
 * `iframe.contentDocument.body.scrollHeight` — same-origin srcdoc allows that
 * read without granting the email any script rights.
 */
export function buildIframeSrcDoc(html: string, opts?: { dark?: boolean }): string {
  // Self-styled emails opt OUT of the dark treatment entirely (see
  // `emailHasOwnCanvas`): light canvas, original colors, in both themes.
  const dark = !!opts?.dark && !emailHasOwnCanvas(html);
  const bg = dark ? '#1f2937' : '#ffffff';
  const fg = dark ? '#e5e7eb' : '#111827';
  const link = dark ? '#93c5fd' : '#2563eb';

  // In dark mode, force the email's text to our light foreground. Emails very
  // commonly hard-code dark text inline (e.g. Outlook emits
  // `<div style="color:rgb(0,0,0)">`), which beats a plain `body { color }` rule
  // and renders black-on-dark. `* { color: fg !important }` overrides those
  // inline colors so the text stays readable; the more specific `a` rule (also
  // !important) wins for links so they don't get flattened to body text.
  const forceText = dark
    ? `
      * { color: ${fg} !important; }
      a, a * { color: ${link} !important; }`
    : '';

  const base = `
    <style>
      html, body { margin: 0; padding: 0; }
      body {
        background: ${bg};
        color: ${fg};
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        padding: 12px 14px;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
      img, table, video, iframe { max-width: 100% !important; height: auto; }
      table { width: auto !important; border-collapse: collapse; }
      a { color: ${link}; }
      pre, code { white-space: pre-wrap; word-break: break-word; }
      * { max-width: 100%; box-sizing: border-box; }${forceText}
    </style>`;

  // If the body is already a full document, inject our base styles into <head>
  // (or before </body>) so the email's own CSS still loads but our wrapping
  // guards (max-width, word-wrap) apply last. Otherwise wrap the fragment.
  const isFullDoc = /<html[\s>]/i.test(html) || /<!doctype/i.test(html);
  if (isFullDoc) {
    if (/<head[\s>]/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${base}`);
    }
    if (/<body[\s>]/i.test(html)) {
      return html.replace(/<body([^>]*)>/i, `<head>${base}</head><body$1>`);
    }
    return `${base}${html}`;
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${base}</head><body>${html}</body></html>`;
}
